import React, { useEffect, useState } from 'react';
import {
  DocumentTextIcon,
  UsersIcon,
  ChartBarIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const DocumentDemographicsDashboard = ({ documents, showDetails, toggleDetails, residentCount, nonResidentCount, stats }) => {
  // By type
  const typeStats = stats.byType ? Object.entries(stats.byType).map(([key, count]) => ({ key, label: key, count })) : [];
  // By status
  const ALL_STATUSES = ['pending','processing','ready','released','cancelled'];
  const statusStats = ALL_STATUSES.map(status => ({
    status: status.charAt(0).toUpperCase() + status.slice(1),
    count: stats.statusCounts && stats.statusCounts[status] !== undefined ? stats.statusCounts[status] : (documents ? documents.filter(d => (d.status || '').toLowerCase() === status).length : 0)
  }));
  // Resident/Non-resident document breakdown
  const residentTypeStats = [
    { label: 'Residents',     count: stats.residentDocs ?? 0,    color: 'text-gray-600' },
    { label: 'Non-Residents', count: stats.nonResidentDocs ?? 0, color: 'text-gray-600' }
  ];

  // Monthly Collection
  const monthlyCollection = stats.monthlyCollection
    ? Object.entries(stats.monthlyCollection).map(([month, total]) => ({ month, total: Number(total).toFixed(2) }))
    : [];

  // Total collection for the past 3 months (from API)
  const totalPast3MonthsCollection = stats.totalPast3MonthsCollection ?? 0;

  return (
    <div className="mb-8">
      <div className="bg-white rounded-lg shadow mb-6">
        <button
          onClick={toggleDetails}
          className="w-full px-6 py-4 flex items-center justify-between text-left transition-all duration-200 rounded-lg"
        >
          <div className="flex items-center">
            <ChartBarIcon className="w-6 h-6 mr-3 text-blue-600" />
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Document Demographics</h4>
              <p className="text-sm text-gray-600">Breakdown by type, status, and applicant gender</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {showDetails ? (
              <ChevronUpIcon className="w-5 h-5" />
            ) : (
              <ChevronDownIcon className="w-5 h-5" />
            )}
          </div>
        </button>

        {showDetails && (
          <div className="space-y-6 p-4 animate-in slide-in-from-top duration-300">
            {/* Breakdown Rows */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* By Type */}
              <div className="bg-blue-50 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Document Type
                </h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {typeStats.length > 0 ? typeStats.map(({ key, label, count }) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{label}</span>
                      <span className="text-sm font-bold text-gray-600">{count}</span>
                    </div>
                  )) : (
                    <div className="text-center text-gray-500 text-sm py-4">No type data</div>
                  )}
                </div>
              </div>

              {/* By Status */}
              <div className="bg-green-50 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-2 text-green-600" />
                  Status
                </h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {statusStats.map(({ status, count }) => (
                    <div key={status} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{status}</span>
                      <span className="text-sm font-bold text-gray-600">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Resident Type */}
              <div className="bg-violet-50 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-2 text-violet-600" />
                  Resident Type
                </h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {residentTypeStats.map(({ label, count, color }) => (
                    <div key={label} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{label}</span>
                      <span className={`text-sm font-bold ${color}`}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly Collection */}
              <div className="bg-red-50 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-2 text-red-600" />
                  Monthly Collection
                </h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {monthlyCollection.length > 0 ? monthlyCollection.map(({ month, total }) => (
                    <div key={month} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{month}</span>
                      <span className="text-sm font-bold text-gray-600">{total}</span>
                    </div>
                  )) : (
                    <div className="text-center text-gray-500 text-sm py-4">No collection data</div>
                  )}
                </div>
                {/* Total for past 3 months */}
                <div className="mt-4 border-t pt-3 flex justify-between items-center">
                  <span className="text-sm text-gray-700 font-semibold">Total <span className="text-gray-400 text-xs">(Past 3 Months)</span> </span>
                  <span className="text-md font-bold text-red-700">{totalPast3MonthsCollection}</span>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default React.memo(DocumentDemographicsDashboard);
